import { BehaviorSubject, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { SimpleDataSource } from './simple-data-source';

export interface DefaultQuery {
  q: string;
}

export interface PageRequest {
  after: string;
  limit: number;
}

// One page with possible continuation token to the next page
export interface Page<T> {
  items: T[];
  next?: string;
}

export type PaginationEndpoint<T, Q> = (pageable: PageRequest, query: Q) => Observable<Page<T>>

export class PaginationDataSource<T, Q = Partial<T>> implements SimpleDataSource<T> {
  private readonly ngDestroy$ = new Subject();

  private nextPage: string;
  private readonly continuationToken = new Subject<string>();
  private readonly query: BehaviorSubject<Q>;

  public page$: Observable<Page<T>>;

  private items$: ReplaySubject<T[]>;
  private items: T[];

  constructor(
    private endpoint: PaginationEndpoint<T, Q>,
    initialQuery: Q,
    public pageSize = 20) {

    this.items = [];
    this.items$ = new ReplaySubject<T[]>();
    this.query = new BehaviorSubject<Q>(initialQuery);
    const param$ = this.query;


    this.page$ = param$.pipe(
      switchMap((query) => this.continuationToken.pipe(
        startWith(""),
        switchMap(after => this.endpoint({after, limit: this.pageSize}, query)
        ),
        tap(page => this.nextPage = page.next),
        tap(page => {
          this.items = this.items.concat(page.items);
          this.items$.next(this.items);
        }),
      )),
      shareReplay(1)
    )
  }

  queryBy(query: Partial<Q>): void {
    const lastQuery = this.query.getValue();
    const nextQuery = {...lastQuery, ...query};
    this.items = [];
    this.query.next(nextQuery);
  }

  fetchNextPage(pageSize?: number): boolean {
    if (pageSize) {
      this.pageSize = pageSize
    }
    if (!this.nextPage || this.nextPage == '') {
      return false;
    }
    this.continuationToken.next(this.nextPage);
    return true;
  }

  connect(): Observable<T[]> {
    this.page$.pipe(
      takeUntil(this.ngDestroy$),
      map(page => page.items)
    ).subscribe();
    return this.items$;
  }

  disconnect(): void {
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }
}
